import { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MapPin, Search, LocateFixed, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Fix default icon paths (Vite doesn't bundle Leaflet's image assets by default)
const defaultIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

interface Props {
  latitude: string;
  longitude: string;
  onChange: (lat: string, lng: string) => void;
  defaultCenter?: [number, number];
  height?: string;
}

const ETHIOPIA_CENTER: [number, number] = [9.03, 38.74]; // Addis Ababa

function ClickHandler({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function Recenter({ center }: { center: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, Math.max(map.getZoom(), 13));
  }, [center[0], center[1]]);
  return null;
}

export function MapLocationPicker({
  latitude,
  longitude,
  onChange,
  defaultCenter = ETHIOPIA_CENTER,
  height = '320px',
}: Props) {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);

  const lat = latitude ? Number(latitude) : null;
  const lng = longitude ? Number(longitude) : null;
  const hasMarker = lat !== null && lng !== null && !Number.isNaN(lat) && !Number.isNaN(lng);

  const center = useMemo<[number, number]>(
    () => (hasMarker ? [lat as number, lng as number] : defaultCenter),
    [hasMarker, lat, lng, defaultCenter[0], defaultCenter[1]],
  );

  const handlePick = (la: number, ln: number) => {
    onChange(la.toFixed(6), ln.toFixed(6));
  };

  const handleSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=et&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        handlePick(parseFloat(data[0].lat), parseFloat(data[0].lon));
      } else {
        toast.error('No results found. Try a more specific address.');
      }
    } catch {
      toast.error('Search failed. Check your connection.');
    } finally {
      setSearching(false);
    }
  };

  const handleLocate = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        handlePick(pos.coords.latitude, pos.coords.longitude);
        setLocating(false);
      },
      () => {
        toast.error('Could not get your location');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
            placeholder="Search address (e.g. Bole Edna Mall, Addis Ababa)"
            className="pl-8"
          />
        </div>
        <Button type="button" variant="secondary" onClick={handleSearch} disabled={searching} className="gap-1.5">
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Search
        </Button>
        <Button type="button" variant="outline" onClick={handleLocate} disabled={locating} className="gap-1.5">
          {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
          Use my location
        </Button>
      </div>

      <div className="rounded-xl overflow-hidden border" style={{ height }}>
        <MapContainer center={center} zoom={hasMarker ? 14 : 6} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
          />
          <ClickHandler onPick={handlePick} />
          {hasMarker && (
            <>
              <Recenter center={center} />
              <Marker
                position={[lat as number, lng as number]}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const m = e.target as L.Marker;
                    const p = m.getLatLng();
                    handlePick(p.lat, p.lng);
                  },
                }}
              />
            </>
          )}
        </MapContainer>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <MapPin className="h-3.5 w-3.5 text-primary" />
        <span>Click the map or drag the pin to set the exact location.</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Latitude</Label>
          <Input
            type="number" step="any"
            value={latitude}
            onChange={(e) => onChange(e.target.value, longitude)}
            placeholder="e.g. 9.0300"
          />
        </div>
        <div>
          <Label className="text-xs">Longitude</Label>
          <Input
            type="number" step="any"
            value={longitude}
            onChange={(e) => onChange(latitude, e.target.value)}
            placeholder="e.g. 38.7400"
          />
        </div>
      </div>
    </div>
  );
}
